import { type Editor, Extension } from '@tiptap/core';
import { PluginKey } from '@tiptap/pm/state';
import Suggestion, { type SuggestionOptions } from '@tiptap/suggestion';
import { createRoot } from 'react-dom/client';
import SuggestionPopover from './suggestion-popover';
import type { SuggestionExtensionConfig, SuggestionListHandle } from './types';

// Per-editor flag so multiple editors on screen don't bleed open-popup state
// into each other (matters in Storybook galleries and dev tools).
const openByEditor = new WeakMap<Editor, boolean>();
export const isSuggestionPopupOpen = (editor: Editor | null | undefined): boolean =>
  editor ? (openByEditor.get(editor) ?? false) : false;

// Lets a config's `items()` callback push a loading indicator into the
// currently open popup — TipTap doesn't expose a "search starting" hook, so we
// bridge it through the list's imperative handle.
const handleByEditor = new WeakMap<Editor, SuggestionListHandle | null>();
export const setSuggestionLoading = (editor: Editor | null | undefined, loading: boolean): void => {
  if (!editor) return;
  handleByEditor.get(editor)?.setLoading(loading);
};

const createRender =
  <TItem,>(config: SuggestionExtensionConfig<TItem>): NonNullable<SuggestionOptions<TItem, TItem>['render']> =>
  () => {
    let container: HTMLDivElement | null = null;
    let root: ReturnType<typeof createRoot> | null = null;
    let activeEditor: Editor | null = null;
    // Stable ref handed to the list; setSuggestionLoading / onKeyDown go through it.
    const listRef: { current: SuggestionListHandle | null } = { current: null };

    const setListRef = (h: SuggestionListHandle | null) => {
      listRef.current = h;
      if (activeEditor) handleByEditor.set(activeEditor, h);
    };

    const renderTree = (props: {
      items: TItem[];
      query: string;
      command: (item: TItem) => void;
      clientRect?: (() => DOMRect | null) | null;
    }) => {
      if (!root) return;
      root.render(
        <SuggestionPopover rect={props.clientRect?.() ?? null} inline={!!config.inlineWhenEmpty && !props.query.trim()}>
          {config.renderList({
            items: props.items,
            query: props.query,
            onSelect: item => props.command(item),
            listRef: setListRef,
          })}
        </SuggestionPopover>,
      );
    };

    return {
      onStart: props => {
        activeEditor = props.editor;
        openByEditor.set(props.editor, true);
        container = document.createElement('div');
        document.body.appendChild(container);
        root = createRoot(container);
        renderTree(props);
      },
      onUpdate: props => {
        renderTree(props);
      },
      onKeyDown: props => {
        if (props.event.key === 'Escape') return true;
        return listRef.current?.onKeyDown(props.event) ?? false;
      },
      onExit: () => {
        if (activeEditor) {
          openByEditor.set(activeEditor, false);
          handleByEditor.delete(activeEditor);
        }
        activeEditor = null;
        listRef.current = null;
        if (container) {
          root?.unmount();
          container.remove();
          container = null;
          root = null;
        }
      },
    };
  };

// Options the extension exposes via `.configure()`. `getIsLive` is resolved at
// search time so `items()` can scope its requests to the env selected in the
// host UI; undefined → the client's global env header.
type SuggestionExtensionOptions = {
  getIsLive?: () => boolean | undefined;
};

export const createSuggestionExtension = <TItem,>(config: SuggestionExtensionConfig<TItem>) =>
  Extension.create<SuggestionExtensionOptions>({
    name: config.name,
    addOptions() {
      return { getIsLive: undefined };
    },
    addProseMirrorPlugins() {
      const editor = this.editor;
      const { getIsLive } = this.options;
      return [
        Suggestion<TItem, TItem>({
          editor,
          pluginKey: new PluginKey(config.name),
          char: config.char ?? '@',
          allowSpaces: config.allowSpaces ?? false,
          command: ({ editor: e, range, props }) => config.command({ editor: e, range, item: props }),
          items: ({ query }) => config.items({ query, editor, isLive: getIsLive?.() }),
          render: createRender(config),
        }),
      ];
    },
  });
